import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Select, ISelectItem } from 'components/UI/Select/Select'
import { EmailContact } from 'models/emailIntegration'
import apiService from 'services/api'

export interface InboxFilters {
  from_email?: string
  to_email?: string
  date_greater?: string
  date_less?: string
  status?: string
  has_attachment?: string
}

interface InboxFilterChipsProps {
  filters: InboxFilters
  onChange: (filters: InboxFilters) => void
}

const STATUS_OPTIONS: ISelectItem<string>[] = [
  { label: 'Replied', value: 'replied' },
  { label: 'Not Replied', value: 'not_replied' },
]

const ATTACHMENT_OPTIONS: ISelectItem<string>[] = [
  { label: 'Has Attachment', value: 'true' },
  { label: 'No Attachment', value: 'false' },
]

const DATE_OPTIONS: ISelectItem<string>[] = [
  { label: 'Last Week', value: 'last_week' },
  { label: 'Last Month', value: 'last_month' },
  { label: 'Last 3 Months', value: 'last_3_months' },
]

/** Format a Date as YYYY-MM-DD. */
function formatDate(date: Date): string {
  return date.toISOString().split('T')[0]
}

/** Compute start/end dates for a date preset key. */
function getDateRange(preset: string): { greater: string; less: string } {
  const end = new Date()
  const start = new Date()
  switch (preset) {
    case 'last_week':
      start.setDate(start.getDate() - 7)
      break
    case 'last_month':
      start.setMonth(start.getMonth() - 1)
      break
    case 'last_3_months':
      start.setMonth(start.getMonth() - 3)
      break
  }
  return { greater: formatDate(start), less: formatDate(end) }
}

/** Convert an EmailContact to a Select item. */
function contactToSelectItem(contact: EmailContact): ISelectItem<string> {
  return {
    label: contact.name ? `${contact.name} (${contact.email})` : contact.email,
    value: contact.email,
  }
}

/** Fetch distinct email contacts and return as Select items. */
function useEmailContactItems(
  type: 'senders' | 'recipients'
): ISelectItem<string>[] {
  const api = apiService()

  const { data } = useQuery({
    queryKey: ['email-contacts', type],
    queryFn: ({ signal }) => {
      const fetcher =
        type === 'senders'
          ? api.emailIntegration.getDistinctSenders
          : api.emailIntegration.getDistinctRecipients
      return fetcher({ search: '' }, signal)
    },
  })

  return useMemo(() => (data || []).map(contactToSelectItem), [data])
}

/** Row of Select filter dropdowns for To, From, Date, Status, and Attachment. */
export function InboxFilterChips({ filters, onChange }: InboxFilterChipsProps) {
  const senderItems = useEmailContactItems('senders')
  const recipientItems = useEmailContactItems('recipients')

  /** Determine which date preset is currently selected (if any). */
  const activeDatePreset = useMemo(() => {
    if (!filters.date_greater && !filters.date_less) return undefined
    for (const option of DATE_OPTIONS) {
      const range = getDateRange(option.value!)
      if (
        range.greater === filters.date_greater &&
        range.less === filters.date_less
      ) {
        return option.value
      }
    }
    return undefined
  }, [filters.date_greater, filters.date_less])

  const selectProps = {
    buttonSize: 'md' as const,
    buttonVariant: 'outline' as const,
    buttonClassName: '!flex-initial',
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Select
        {...selectProps}
        items={recipientItems}
        selected={filters.to_email}
        onSelect={(value) => onChange({ ...filters, to_email: value })}
        buttonLabel="To"
        searchPlaceholder="Search recipients..."
        nullSelectionLabel="All"
      />
      <Select
        {...selectProps}
        items={senderItems}
        selected={filters.from_email}
        onSelect={(value) => onChange({ ...filters, from_email: value })}
        buttonLabel="From"
        searchPlaceholder="Search senders..."
        nullSelectionLabel="All"
      />
      <Select
        {...selectProps}
        items={DATE_OPTIONS}
        selected={activeDatePreset}
        onSelect={(value) => {
          if (value) {
            const range = getDateRange(value)
            onChange({
              ...filters,
              date_greater: range.greater,
              date_less: range.less,
            })
          } else {
            const next = { ...filters }
            delete next.date_greater
            delete next.date_less
            onChange(next)
          }
        }}
        buttonLabel="Date"
        nullSelectionLabel="Any Time"
      />
      <Select
        {...selectProps}
        items={STATUS_OPTIONS}
        selected={filters.status}
        onSelect={(value) => onChange({ ...filters, status: value })}
        buttonLabel="Status"
        nullSelectionLabel="All"
      />
      <Select
        {...selectProps}
        items={ATTACHMENT_OPTIONS}
        selected={filters.has_attachment}
        onSelect={(value) => onChange({ ...filters, has_attachment: value })}
        buttonLabel="Attachments"
        nullSelectionLabel="All"
      />
    </div>
  )
}
